// ============================================================================
// CASE STATE DATA HOOKS
// ============================================================================

import { useCallback, useEffect, useState } from 'react';
import { caseStateDataService } from '../../api/services/caseStateDataService';
import type {
  CaseStateData,
  CaseStateDataQueryParameters,
  SaveCaseStateDataRequest,
} from '../../api/types/caseStateData.types';
import type { ID } from '../../api/types';

/**
 * Loads case state-data rows for a case (and optionally a specific stateCode).
 * Defaults to latest-per-state. Pass `allEntries: true` to fetch the full
 * history (used by multi-entry views like the auctions / valuations list).
 */
export const useCaseStateData = (params: CaseStateDataQueryParameters | null | undefined) => {
  const [data, setData] = useState<CaseStateData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const paramsKey = params ? JSON.stringify(params) : null;

  const fetchRows = useCallback(async () => {
    if (!params) { setData([]); return; }
    setLoading(true); setError(null);
    try {
      const response = await caseStateDataService.list(params);
      setData(response.data ?? []);
    } catch (err) {
      setError(err as Error);
      setData([]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  return { data, loading, error, refetch: fetchRows };
};

/** Fetch the latest entry for a single (case, state) pair. Returns null when no entry exists. */
export const useLatestCaseStateData = (caseId: ID | null | undefined, stateCode: string | null | undefined) => {
  const enabled = !!caseId && !!stateCode;
  const { data, loading, error, refetch } = useCaseStateData(
    enabled ? { caseId: caseId!, stateCode: stateCode!, allEntries: false } : null,
  );
  return {
    entry: data[0] ?? null,
    loading,
    error,
    refetch,
  };
};

export const useSaveCaseStateData = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const save = useCallback(async (
    request: SaveCaseStateDataRequest,
    options?: { onSuccess?: (entry: CaseStateData) => void; onError?: (err: Error) => void },
  ) => {
    setLoading(true); setError(null);
    try {
      const response = await caseStateDataService.save(request);
      options?.onSuccess?.(response.data);
      return response.data;
    } catch (err) {
      const e = err as Error;
      setError(e);
      options?.onError?.(e);
      throw e;
    } finally {
      setLoading(false);
    }
  }, []);

  return { save, loading, error };
};
